import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TagPoolField } from "@/components/tags/TagPoolField";
import { useToast } from "@/hooks/use-toast";
import clientService from "@/services/client";
import companyService from "@/services/company";
import projectService from "@/services/project";
import { Client, ClientFormData, CompanyMembership } from "@/types/client";
import { Company } from "@/types/company";
import { Project } from "@/types/project";
import { Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

function isClientActive(c?: Client): boolean {
  if (!c) return true;
  if (c.status === "active") return true;
  if (c.status === "inactive" || c.status === "pending") return false;
  return c.isActive !== false;
}

interface MembershipRow {
  companyId: string;
  projectId: string;
}

/** Um cliente pode pertencer a mais de uma empresa (e, em cada uma, a um projeto). Sem vínculo
 * nenhum, mostramos uma linha vazia para o usuário poder escolher a primeira empresa. */
function initialMemberships(c?: Client): MembershipRow[] {
  const memberships = c?.companyMemberships || [];
  if (!memberships.length) return [{ companyId: "", projectId: "" }];
  return memberships.map((m) => ({ companyId: m.companyId, projectId: m.projectId ?? "" }));
}

/** Contatos antigos (de antes de existir a lista) só têm `email`/`phone` escalar — sem este
 * fallback eles apareceriam com a lista vazia mesmo já tendo um valor cadastrado. */
function initialEmails(c?: Client): string[] {
  if (c?.emails?.length) return c.emails;
  return c?.email ? [c.email] : [""];
}
function initialPhones(c?: Client): string[] {
  if (c?.phones?.length) return c.phones;
  return c?.phone ? [c.phone] : [""];
}

interface ClientFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: Client;
  mode: "create" | "edit";
}

export default function ClientForm({
  open,
  onClose,
  onSuccess,
  initialData,
  mode,
}: ClientFormProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: initialData?.name ?? "",
    emails: initialEmails(initialData),
    phones: initialPhones(initialData),
    password: "",
    isActive: isClientActive(initialData),
    tags: initialData?.tags ?? ([] as string[]),
  });
  const [memberships, setMemberships] = useState<MembershipRow[]>(initialMemberships(initialData));
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projectsByCompany, setProjectsByCompany] = useState<Record<string, Project[]>>({});

  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setFormData({
        name: initialData?.name ?? "",
        emails: initialEmails(initialData),
        phones: initialPhones(initialData),
        password: "",
        isActive: isClientActive(initialData),
        tags: initialData?.tags ?? [],
      });
      setMemberships(initialMemberships(initialData));
    }
    prevOpen.current = open;
  }, [open, initialData]);

  useEffect(() => {
    if (!open) return;
    companyService.getAll(undefined, 1, 200).then(([data]) => {
      setCompanies(data?.result || []);
    });
  }, [open]);

  const companyIds = memberships.map((m) => m.companyId).filter(Boolean);
  const companyIdsKey = [...new Set(companyIds)].sort().join(",");
  useEffect(() => {
    const missing = [...new Set(companyIds)].filter((id) => !projectsByCompany[id]);
    if (!missing.length) return;
    missing.forEach((companyId) => {
      projectService.getAllByClient(companyId).then(([data]) => {
        setProjectsByCompany((prev) => ({ ...prev, [companyId]: data?.result || [] }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIdsKey]);

  const setForm = (patch: Partial<typeof formData>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  };

  const updateMembershipAt = (idx: number, patch: Partial<MembershipRow>) => {
    setMemberships((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };
  const addMembership = () =>
    setMemberships((prev) => [...prev, { companyId: "", projectId: "" }]);
  const removeMembership = (idx: number) =>
    setMemberships((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : [{ companyId: "", projectId: "" }]));

  const updateEmailAt = (idx: number, value: string) => {
    setFormData((prev) => {
      const emails = [...prev.emails];
      emails[idx] = value;
      return { ...prev, emails };
    });
  };
  const addEmail = () => setForm({ emails: [...formData.emails, ""] });
  const removeEmail = (idx: number) =>
    setForm({ emails: formData.emails.filter((_, i) => i !== idx) });

  const updatePhoneAt = (idx: number, value: string) => {
    setFormData((prev) => {
      const phones = [...prev.phones];
      phones[idx] = value;
      return { ...prev, phones };
    });
  };
  const addPhone = () => setForm({ phones: [...formData.phones, ""] });
  const removePhone = (idx: number) =>
    setForm({ phones: formData.phones.filter((_, i) => i !== idx) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome é obrigatório",
        variant: "destructive",
      });
      return;
    }
    const emails = formData.emails.map((e) => e.trim()).filter(Boolean);
    const phones = formData.phones.map((p) => p.trim()).filter(Boolean);
    if (!emails.length && !phones.length) {
      toast({
        title: "Erro",
        description: "Informe e-mail ou telefone",
        variant: "destructive",
      });
      return;
    }
    if (mode === "create" && !formData.password.trim()) {
      toast({
        title: "Erro",
        description: "Senha é obrigatória para novo usuário",
        variant: "destructive",
      });
      return;
    }

    const companyMemberships: CompanyMembership[] = memberships
      .filter((m) => m.companyId)
      .map((m) => ({ companyId: m.companyId, projectId: m.projectId || undefined }));

    const payload: ClientFormData = {
      name: formData.name.trim(),
      emails,
      phones,
      isActive: formData.isActive,
      tags: formData.tags,
      companyMemberships,
      ...(mode === "create" && formData.password
        ? { password: formData.password }
        : {}),
    };

    setLoading(true);
    try {
      if (mode === "create") {
        const [, error] = await clientService.create(payload);
        if (error) {
          toast({
            title: "Erro",
            description: error.msg || "Erro ao criar usuário",
            variant: "destructive",
          });
          return;
        }
        toast({ title: "Sucesso", description: "Usuário cliente criado" });
      } else if (initialData) {
        const [, error] = await clientService.update(initialData._id, payload);
        if (error) {
          toast({
            title: "Erro",
            description: error.msg || "Erro ao atualizar",
            variant: "destructive",
          });
          return;
        }
        toast({ title: "Sucesso", description: "Usuário atualizado" });
      }
      onSuccess();
      onClose();
    } catch {
      toast({
        title: "Erro",
        description: "Falha na requisição",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Novo usuário cliente" : "Editar usuário cliente"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Cria um login de portal com permissão de cliente nesta organização."
                : "Atualize nome, e-mail, telefone e status."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setForm({ name: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email-0">E-mail</Label>
              {formData.emails.map((email, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    id={`email-${idx}`}
                    type="email"
                    value={email}
                    onChange={(e) => updateEmailAt(idx, e.target.value)}
                    placeholder="email@exemplo.com"
                    autoComplete="email"
                  />
                  {formData.emails.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEmail(idx)}
                      aria-label="Remover e-mail"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={addEmail}
              >
                <Plus className="h-4 w-4 mr-1" /> Adicionar e-mail
              </Button>
            </div>
            {mode === "create" && (
              <div className="grid gap-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setForm({ password: e.target.value })}
                  placeholder="Senha de acesso ao portal"
                  autoComplete="new-password"
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="phone-0">Telefone</Label>
              {formData.phones.map((phone, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    id={`phone-${idx}`}
                    type="tel"
                    value={phone}
                    onChange={(e) => updatePhoneAt(idx, e.target.value)}
                    placeholder="Opcional"
                  />
                  {formData.phones.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePhone(idx)}
                      aria-label="Remover telefone"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={addPhone}
              >
                <Plus className="h-4 w-4 mr-1" /> Adicionar telefone
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Empresas</Label>
              {memberships.map((m, idx) => {
                const projects = m.companyId ? projectsByCompany[m.companyId] || [] : [];
                const availableCompanies = companies.filter(
                  (c) => c._id === m.companyId || !companyIds.includes(c._id)
                );
                return (
                  <div key={idx} className="flex gap-2">
                    <Select
                      value={m.companyId || "__none__"}
                      onValueChange={(v) =>
                        updateMembershipAt(idx, {
                          companyId: v === "__none__" ? "" : v,
                          projectId: "",
                        })
                      }
                    >
                      <SelectTrigger id={`companyId-${idx}`} className="flex-[2]">
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nenhuma</SelectItem>
                        {availableCompanies.map((c) => (
                          <SelectItem key={c._id} value={c._id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={m.projectId || "__none__"}
                      onValueChange={(v) =>
                        updateMembershipAt(idx, { projectId: v === "__none__" ? "" : v })
                      }
                      disabled={!m.companyId}
                    >
                      <SelectTrigger id={`projectId-${idx}`} className="flex-[2]">
                        <SelectValue
                          placeholder={m.companyId ? "Projeto (opcional)" : "Escolha a empresa"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nenhum</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p._id} value={p._id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {memberships.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMembership(idx)}
                        aria-label="Remover empresa"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={addMembership}
              >
                <Plus className="h-4 w-4 mr-1" /> Adicionar empresa
              </Button>
            </div>
            <TagPoolField id="client-tags" value={formData.tags} onChange={(tags) => setForm({ tags })} />
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="isActive">Ativo</Label>
                <p className="text-xs text-muted-foreground">
                  Inativos não acessam o portal.
                </p>
              </div>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setForm({ isActive: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Salvando..." : mode === "create" ? "Criar" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
